import React, { useEffect } from "react";
import { useFieldArray, Control, FieldErrors, useWatch } from "react-hook-form";
import { Button, Card, Col, Row, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { InputField, ReactSelectField } from "../../atoms/FormElement";
import { useDropdownOptions } from "../../../hooks/useFormattedSelectOptions";
import { PopupConfirm } from "../../atoms/Popup";
import { DeleteIcon } from "../../../assets/svg";

interface BankDetailSectionProps {
  control: Control<any>;
  errors: FieldErrors;
  configData: any;
  getValidationProps: Function;
  loading?: boolean;
  ownerId?: string;
  result?: any;
}

let appendValues = {
  beneficiaryBank: "",
  beneficiaryAccountNumber: "",
  beneficiaryIBAN: "",
  beneficiaryName: "",
  address: "",
  branch: "",
  swiftCode: "",
  isDeleted: false,
};
const AddOwnerBankDetail: React.FC<BankDetailSectionProps> = ({
  control,
  errors,
  configData,
  getValidationProps,
  loading,
  ownerId,
  result,
}) => {
  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name: "beneficiaries",
  });

  const bankOptions = useDropdownOptions(configData?.bankList);
  // Watch the array to reflect isDeleted state changes
  const beneficiaries = useWatch({ control, name: "beneficiaries" });
  const defaultProps = { control };

  const removeAndDeleteBeneficiary = (index) => {
    PopupConfirm({
      title: `Are you sure you want to delete this beneficiary?`,
      onOk: () => {
        ownerId
          ? update(index, {
              ...beneficiaries[index],
              isDeleted: true,
            })
          : remove(index);
      },
      okText: "Yes",
    });
  };

  useEffect(() => {
    const data = result?.result?.data || [];

    if (data.length > 0) {
      // ✅ Only pick required fields
      const formatted =
        data.map((item) => ({
          beneficiaryBank: {
            label:
              item.beneficiaryBank
                ?.toString()
                ?.split("_")
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ") || "",
            value: item.beneficiaryBank || "",
          },
          beneficiaryAccountNumber: item.beneficiaryAccountNumber || "",
          beneficiaryIBAN: item.beneficiaryIBAN || "",
          beneficiaryName: item.beneficiaryName || "",
          address: item.address || "",
          branch: item.branch || "",
          swiftCode: item.swiftCode || "",
          isDeleted: item?.isDeleted,
        })) || [];
      replace(formatted);
    }
  }, [result]);

  return (
    <Card loading={loading} title="Bank Accounts" style={{ marginBottom: 10 }}>
      {fields.map((field, index) => {
        const isDeleted = beneficiaries?.[index]?.isDeleted;

        // Skip rendering deleted entries
        if (isDeleted) return null;

        return (
          <Card key={field.id} style={{ marginBottom: 10 }}>
            {
              <div style={{ position: "absolute", right: "20px", top: "10px" }}>
                <Tooltip title="Delete" trigger="hover" showArrow={false}>
                  <Button type="link" className="ef-action-btn" onClick={() => removeAndDeleteBeneficiary(index)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </div>
            }
            <Row gutter={8} align="middle">
              <Col span={12}>
                <InputField
                  fieldname={`beneficiaries.${index}.beneficiaryName`}
                  label="Beneficiary Name (Account Title)"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.beneficiaryName`,
                    errors,
                    isRequired: true,
                  })}
                  iProps={{
                    placeholder: "Enter beneficiary name",
                  }}
                />
              </Col>

              <Col span={12}>
                <ReactSelectField
                  fieldname={`beneficiaries.${index}.beneficiaryBank`}
                  label="Bank"
                  selectOption={bankOptions || []}
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.beneficiaryBank`,
                    errors,
                    isRequired: true,
                  })}
                  iProps={{
                    placeholder: "Select bank",
                  }}
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldname={`beneficiaries.${index}.beneficiaryAccountNumber`}
                  label="Account Number"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.beneficiaryAccountNumber`,
                    errors,
                    isRequired: true,
                  })}
                  iProps={{
                    placeholder: "Enter account number",
                  }}
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldname={`beneficiaries.${index}.beneficiaryIBAN`}
                  label="IBAN"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.beneficiaryIBAN`,
                    errors,
                    isRequired: true,
                  })}
                  iProps={{
                    placeholder: "Enter IBAN",
                  }}
                />
              </Col>

              <Col span={8}>
                <InputField
                  fieldname={`beneficiaries.${index}.branch`}
                  label="Branch"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.branch`,
                    errors,
                    isRequired: false,
                  })}
                  iProps={{
                    placeholder: "Enter Branch",
                  }}
                />
              </Col>
              <Col span={8}>
                <InputField
                  fieldname={`beneficiaries.${index}.address`}
                  label="Address"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.address`,
                    errors,
                    isRequired: false,
                  })}
                  iProps={{
                    placeholder: "Enter Address",
                  }}
                />
              </Col>
              <Col span={8}>
                <InputField
                  fieldname={`beneficiaries.${index}.swiftCode`}
                  label="Swift Code"
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: `beneficiaries.${index}.swiftCode`,
                    errors,
                    isRequired: false,
                  })}
                  iProps={{
                    placeholder: "Enter Swift Code",
                  }}
                />
              </Col>
            </Row>
          </Card>
        );
      })}

      <Button type="dashed" onClick={() => append(appendValues)} icon={<PlusOutlined />} block>
        Add Bank
      </Button>
    </Card>
  );
};

export default AddOwnerBankDetail;
